/**
 * Durable storage for LIVE-captured listing photos.
 *
 * WHY THIS EXISTS. `takePictureAsync` writes into the app's CACHE directory,
 * which Android may evict at any moment, and the listing wizard held those URIs
 * in memory only. So a host who shot all 17 required photos and hit a failure at
 * publish — exactly what happened on 2026-08-16, when every upload died on a
 * native hashing bug — was told "guardamos el vehículo como borrador, no tenés
 * que empezar desde cero" while the photos existed nowhere: not on the server
 * (the upload had failed) and not durably on the phone. Reopening the app meant
 * retaking all 17. The promise in that message was false.
 *
 * Copying each capture into `Paths.document` (documented as "safe from being
 * deleted by the system") is what makes persisting the URI meaningful: a saved
 * path is worthless if the file behind it can vanish.
 *
 * PROVENANCE IS UNCHANGED. Qualitas requires the photos be taken live, and they
 * still are: this runs AFTER LiveCameraModal returns, copies the very bytes the
 * camera produced, and never touches `source` ('tago_live_camera'), `capturedAt`
 * or the SHA-256 recorded at upload. Durability is about not losing a live
 * photo, never about accepting one from the gallery.
 *
 * Every function FAILS OPEN: if the copy cannot be made we return the original
 * cache URI, because a photo in a fragile place beats no photo at all.
 */
import { Platform } from 'react-native'
import { Directory, File, Paths } from 'expo-file-system'
import * as Crypto from 'expo-crypto'

/** Sub-directory of the document dir holding in-progress listing captures. */
const LISTING_PHOTO_DIR = 'listing-photos'

function listingPhotoDirectory(): Directory {
  const dir = new Directory(Paths.document, LISTING_PHOTO_DIR)
  if (!dir.exists) dir.create({ idempotent: true })
  return dir
}

/** True for a URI that already lives in our durable directory. */
export function isDurableListingPhotoUri(uri: string): boolean {
  return uri.includes(`/${LISTING_PHOTO_DIR}/`)
}

/**
 * Copy a freshly captured photo out of the cache into durable storage.
 *
 * Returns the durable URI, or the original URI if the copy is impossible (web,
 * missing source file, out of space). Never throws: losing the capture because
 * we could not file it away would be worse than the fragility we are fixing.
 */
export function persistListingCapture(uri: string): string {
  // Web keeps blob:/data: URIs that the File API cannot copy, and the browser
  // holds them for the session — nothing to do.
  if (Platform.OS === 'web') return uri
  if (!uri || isDurableListingPhotoUri(uri)) return uri

  try {
    const source = new File(uri)
    if (!source.exists) return uri

    // Preserve the extension so downstream MIME/format detection is unaffected.
    const extension = source.name?.includes('.')
      ? source.name.slice(source.name.lastIndexOf('.'))
      : '.jpg'
    const destination = new File(listingPhotoDirectory(), `${Crypto.randomUUID()}${extension}`)
    source.copy(destination)
    return destination.exists ? destination.uri : uri
  } catch {
    return uri
  }
}

/**
 * Does the file behind a persisted capture still exist?
 *
 * A restored draft must be TRUTHFUL: showing 17 filled slots whose files are
 * gone (app data cleared, or a pre-durability capture left in the cache) would
 * just move the failure to publish, which is the exact dishonesty this whole
 * change exists to remove. Unknown/unreadable → treated as present (fail open),
 * since the upload itself is the authoritative check.
 */
export function listingPhotoExists(uri: string): boolean {
  if (Platform.OS === 'web') return true
  if (!uri) return false
  if (!uri.startsWith('file://') && !uri.startsWith('/')) return true
  try {
    return new File(uri).exists
  } catch {
    return true
  }
}

/**
 * Delete durable captures that are no longer referenced by the draft.
 *
 * Called when a listing is published or the wizard is reset, so abandoned drafts
 * cannot accumulate 17 full-resolution photos per attempt on the host's device.
 * Silent on failure — leftover bytes are a storage nuisance, never a user-facing
 * error.
 */
export function pruneListingPhotoFiles(keepUris: readonly string[]): void {
  if (Platform.OS === 'web') return
  // Never pull files out from under an in-flight upload.
  if (uploadInFlight) return
  try {
    const keep = new Set(keepUris)
    const dir = new Directory(Paths.document, LISTING_PHOTO_DIR)
    if (!dir.exists) return
    for (const entry of dir.list()) {
      if (entry instanceof File && !keep.has(entry.uri)) entry.delete()
    }
  } catch {
    // Best-effort cleanup only.
  }
}

/**
 * Is a publish currently reading these files?
 *
 * A confirmed discard must never sweep the directory while the upload loop is
 * mid-read: the host taps X during a 120s publish, the files vanish, and the
 * uploads that were about to land start failing on a file that existed a moment
 * ago. Module-level because the flight outlives any one component.
 */
let uploadInFlight = false
export function setListingUploadInFlight(active: boolean): void {
  uploadInFlight = active
}

/**
 * Delete ONE orphaned capture — the file a retake or an extra-removal just
 * dropped from the draft.
 *
 * Deliberately targeted, never a directory sweep: a byte-for-byte uri mismatch
 * between dir.list() and what the draft holds would delete photos the host still
 * needs. Silent on failure; a leftover file is a storage nuisance, never an error.
 *
 * Honours the in-flight guard for the same reason the sweep does. A retake while
 * a publish is running would otherwise delete a file the upload loop has not read
 * yet — the loop is sequential over 17 shots, so a photo it is "about to" send is
 * a photo that still has to exist. Skipping the delete only leaks bytes; doing it
 * loses the shot.
 */
export function deleteListingPhotoFile(uri: string): void {
  if (Platform.OS === 'web') return
  if (uploadInFlight) return
  if (!uri || !isDurableListingPhotoUri(uri)) return
  try {
    const file = new File(uri)
    if (file.exists) file.delete()
  } catch {
    // best effort
  }
}

/**
 * The fixed-size identity of a capture's local uri.
 *
 * Exists because on web the uri IS the photo — a `data:` string of megabytes — and anything
 * keyed by it (the staged-upload map) persists in the draft's LIGHT localStorage partition.
 * Sixty-four hex characters carry the identity; the megabytes stay where they belong.
 */
export async function stagedSourceKey(uri: string): Promise<string> {
  return Crypto.digestStringAsync(Crypto.CryptoDigestAlgorithm.SHA256, uri)
}
